import { Link, useParams } from "react-router-dom";
import type { Ability } from "../ability";
import type { EventDetailPayload } from "../api";
import { isWithheldText, wordsOf } from "../api";
import { toneFor } from "../components/tone";
import { Days, Money, label } from "../components/Money";
import { Loading, Refused } from "../components/Shell";
import { useApi } from "../useApi";
import { Scrolls } from "../components/Table";

/** States where the outcome has already happened. Mirrors the engine's
 *  TERMINAL_STATES; a clock in one of these has no deadline left to meet. */
const TERMINAL = new Set([
  "deemed", "answered_in_time", "answered_late", "time_barred", "preserved",
]);

export function EventDetail({ ability }: { ability: Ability }) {
  const { reference = "" } = useParams();
  const { data, error, loading } = useApi<EventDetailPayload>(`/events/${encodeURIComponent(reference)}`);
  if (loading) return <Loading />;
  if (error) return <Refused status={error.status} detail={error.detail} />;
  if (!data) return null;
  const { event, clocks, correspondence, actions } = data;
  // The API sorts resolved clocks last, so the lead is the soonest deadline
  // that can still be met. If every clock on the event is resolved there is
  // no deadline to show — a countdown on a right that has already gone reads
  // as "act now" when there is nothing to do.
  const lead = clocks.find((c) => !TERMINAL.has(c.state));
  const resolved = clocks.find((c) => TERMINAL.has(c.state));

  return (
    <>
      <nav className="hint" style={{ marginBottom: 14 }}>
        <Link to="/radar">Radar</Link> › <span className="mn">{event.reference}</span>
      </nav>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 24, alignItems: "flex-start" }}>
        <div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
            <span className="mn" style={{ fontSize: 18, fontWeight: 600 }}>{event.reference}</span>
            <span className="pill">{label(event.event_type)}</span>
          </div>
          <h1>{event.title}</h1>
        </div>
        {lead ? (
          /* Loud only where an outcome is running towards this event on its
             own. Being late is serious and is not the same thing, so it
             reads high rather than as an alarm. */
          <div className={`card reading reading--${toneFor({
            state: lead.state, deemed_date: lead.deemed_date,
            days: lead.days_to_next_deadline })}`}
            style={{ margin: 0, textAlign: "right", flexShrink: 0 }}>
            <div className="k">Next deadline</div>
            <div className="reading__value" style={{ fontSize: 30, fontWeight: 600 }}>
              <Days value={lead.days_to_next_deadline} />
            </div>
            <div className="mn withheld" style={{ fontSize: 11 }}>
              {lead.next_deadline_date ?? "—"} · clause {lead.clause}
            </div>
          </div>
        ) : resolved ? (
          /* Nothing outstanding. Stated in the past tense and in the
             ordinary ink, because there is no deadline left to meet. */
          <div className="card" style={{ margin: 0, textAlign: "right", flexShrink: 0 }}>
            <div className="k">No deadline outstanding</div>
            <div style={{ fontSize: 15, fontWeight: 600, margin: "6px 0 4px" }}>
              {label(resolved.state)}
            </div>
            <div className="mn withheld" style={{ fontSize: 11 }}>
              {resolved.next_deadline_date ?? "—"} · clause {resolved.clause}
            </div>
          </div>
        ) : null}
      </div>

      <div className="split">
        <div>
          <section className="card">
            <h2 className="secttl">Clocks on this event</h2>
            <Scrolls>
              <table>
                <thead><tr><th>Clause</th><th>State</th><th className="num">Due</th><th className="num">Days</th><th>Act</th></tr></thead>
                <tbody>
                  {clocks.map((c: any) => (
                    <tr key={c.id}>
                      <td className="mn">{c.clause}</td>
                      <td>
                        <span className={`pill ${{
                          now: "alarm", high: "act", spent: "done", open: "",
                        }[toneFor(c)]}`}>{label(c.state)}</span>
                      </td>
                      <td className="num mn">{c.next_deadline_date ?? "—"}</td>
                      <td className="num mn"><Days value={c.days_to_next_deadline} /></td>
                      <td>
                        {ability.canActOn(c.responsible_party)
                          ? <Link to="/board">Respond</Link>
                          : <span className="withheld" title={`${c.responsible_party.replace(/_/g, " ")} must act`}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Scrolls>
          </section>

          <section className="card">
            <h2 className="secttl">Correspondence linked to this event</h2>
            {correspondence.length ? (
              <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {correspondence.map((l: any) => (
                  <li key={l.id} style={{ display: "grid", gridTemplateColumns: "92px 1fr",
                    gap: 12, paddingBottom: 15 }}>
                    <span className="mn withheld" style={{ fontSize: 12, textAlign: "right" }}>{l.sent_date}</span>
                    <div>
                      <div style={{ fontSize: 14 }}>{l.subject}</div>
                      <div style={{ display: "flex", gap: 9, marginTop: 5, alignItems: "center" }}>
                        <span className="pill">{label(l.category)}</span>
                        <span className="mn withheld" style={{ fontSize: 11 }}>
                          confidence {l.confidence.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            ) : <p className="empty">No correspondence has been linked to this event yet.</p>}
          </section>
        </div>

        <aside>
          <section className="card">
            <h2 className="secttl">Exposure</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <div className="k">Prices</div>
                <div className="mn" style={{ fontSize: 21, fontWeight: 600 }}>
                  <Money value={event.value_gbp} />
                </div>
              </div>
              <div>
                <div className="k">Completion</div>
                <div className="mn" style={{ fontSize: 21, fontWeight: 600 }}>
                  {event.time_impact_days ? `+${event.time_impact_days} days` : "—"}
                </div>
              </div>
            </div>
            {!ability.can("view_financials") && (
              <p className="hint">Withheld — your role does not include financial visibility.</p>
            )}
          </section>

          {actions.length > 0 && (
            <section className="card">
              {/* Two kinds of thing live here: a letter waiting for somebody
                  to send it, and a record of an outcome that has already
                  taken effect. Filing the second under "drafted and waiting"
                  and stamping it "unconfirmed" says a letter is pending when
                  none exists and none should. */}
              <h2 className="secttl">
                {actions.some((a) => wordsOf(a.draft_body) || isWithheldText(a.draft_body))
                  ? "Follow-up" : "On the record"}
              </h2>
              {actions.map((a) => (
                <div key={a.id} className="card" style={{ margin: "0 0 8px", padding: "11px 13px" }}>
                  <div style={{ fontSize: 14 }}>{wordsOf(a.draft_subject) || a.title}</div>
                  <div style={{ display: "flex", gap: 9, marginTop: 5, flexWrap: "wrap" }}>
                    {wordsOf(a.draft_body)
                      ? <span className="pill act">{label(a.confirmation)}</span>
                      : isWithheldText(a.draft_body)
                        ? <span className="pill" title="An unsent letter is its author's side's position. It is on the record once it is sent.">being drafted by the other side</span>
                        : <span className="pill">no letter</span>}
                    <span className="mn withheld" style={{ fontSize: 11 }}>owner {a.owner}</span>
                  </div>
                  {!wordsOf(a.draft_body) && !isWithheldText(a.draft_body) && a.detail && (
                    <div className="hint" style={{ marginTop: 7, fontSize: 12 }}>{a.detail}</div>
                  )}
                </div>
              ))}
              <Link className="btn" style={{ display: "block", textAlign: "center", marginTop: 12 }}
                to="/board">Open in the follow-up board</Link>
            </section>
          )}

          <section className="card">
            <h2 className="secttl">Assessment</h2>
            <Link className="btn" style={{ display: "block", textAlign: "center" }}
              to="/impact">Schedule impact for this event</Link>
          </section>
        </aside>
      </div>
    </>
  );
}
